/**
 * iQuickCalc Application Configuration
 */

import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, RichSummary } from '../shared';

export const APP_NAME = 'iQuickCalc';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

// Load type options
export const LOAD_TYPES: DropdownOption[] = [
  { value: '0', label: 'Triangular Load' },
  { value: '1', label: 'Triangular Pulse Load' },
  { value: '2', label: 'Rectangular Load' },
  { value: '3', label: 'Gradually Applied Load' }
];

/**
 * API Payload Type
 */
export interface APIPayload {
  title: string;
  length: string;
  tribWidth: string;
  hingeLength: string;
  totalWeight: string;
  klmFactor: string;
  Ru: string;
  Xe: string;
  LoadType: string;
  Pa: string;
  td: string;
}

export type ChartKey = 'chart_load' | 'chart_duct' | 'chart_response' | 'chart_rebound';

export const PRINT_CHART_ORDER: ChartKey[] = ['chart_load', 'chart_duct', 'chart_response', 'chart_rebound'];

export const PRINT_CHART_ASPECT = {
  chart_load: 'wide',
  chart_duct: 'square',
  chart_response: 'square',
  chart_rebound: 'square'
} satisfies Record<ChartKey, 'wide' | 'square'>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<'wide' | 'square', LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 20 } },
      margin: { l: 86, r: 28, t: 72, b: 108 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  },
  square: {
    width: 600,
    height: 600,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 20 } },
      margin: { l: 88, r: 28, t: 78, b: 114 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(18rem, 70vw, 25rem)';

export const LIVE_PLOT_SIZE_BY_KEY = {
  chart_load: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    maxHeight: '100%',
    aspectRatio: '4 / 3'
  },
  chart_duct: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '4 / 5'
  },
  chart_response: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    maxHeight: '100%',
    aspectRatio: '4 / 5'
  },
  chart_rebound: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    maxHeight: '100%',
    aspectRatio: '4 / 5'
  }
} satisfies Record<ChartKey, LivePlotSizeOptions>;

/**
 * API Response Type
 */
export interface APIResponse {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
}

// Frontend state for this page after analysis. The API returns summary + plotData only;
// `charts` is generated later from mounted LivePlot components for print/save.
export type QuickCalcResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};
